// Staff weapon masteries view - list and select for editing.
//
// Mirrors staff-spells-view.js for consistency. The backend supports an upsert
// POST plus a GET-all/GET-by-slug pair, but no DELETE — so this view is list-
// and-edit only, no delete affordance. Add one if/when the backend grows it.

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';

const pencilIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path
    d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"
  />
</svg>`;

const clipboardIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
</svg>`;

/** @typedef {import('../../types/jsdoc-types.js').WeaponMastery} WeaponMastery */

class StaffWeaponMasteriesView extends BaseComponent {
  static properties = {
    _masteries: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _error: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }
  `;

  constructor() {
    super();
    /** @type {Array<WeaponMastery>} */
    this._masteries = [];
    this._loading = true;
    this._error = null;
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._loadMasteries();
  }

  async _loadMasteries() {
    this._loading = true;
    this._error = null;

    try {
      this._masteries = await api.get('/api/staff/weapon-masteries');
    } catch (err) {
      console.error('Failed to load weapon masteries:', err);
      this._error = /** @type {Error} */ (err).message || 'Failed to load weapon masteries';
    } finally {
      this._loading = false;
    }
  }

  _goToNewMastery() {
    router.navigate('/staff/weapon-masteries/new');
  }

  /** @param {string} slug */
  _editMastery(slug) {
    router.navigate(`/staff/weapon-masteries/${slug}/edit`);
  }

  /** @param {string} description */
  _truncateDescription(description, maxLength = 100) {
    if (!description) return '';
    if (description.length <= maxLength) return description;
    return `${description.substring(0, maxLength)}...`;
  }

  /**
   * @param {string} text
   * @param {Event} event
   */
  _copyToClipboard(text, event) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        const btn = /** @type {HTMLElement} */ (event.target).closest('button');
        if (btn) {
          btn.classList.add('btn-success');
          btn.classList.remove('btn-outline-secondary');
          setTimeout(() => {
            btn.classList.remove('btn-success');
            btn.classList.add('btn-outline-secondary');
          }, 2000);
        }
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
      });
  }

  render() {
    return html`
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">Staff</li>
          <li class="breadcrumb-item active" aria-current="page">Weapon Masteries</li>
        </ol>
      </nav>

      ${this._error ? html` <div class="alert alert-danger">${this._error}</div> ` : ''}

      <div class="d-flex justify-content-between align-items-center mb-3">
        <h2>Weapon Masteries</h2>
        <button class="btn btn-primary" @click="${this._goToNewMastery}">Create Weapon Mastery</button>
      </div>

      ${
        this._loading
          ? html`
            <div class="text-center py-4">
              <div class="spinner-border">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
          `
          : this._masteries.length === 0
            ? html` <p class="text-muted">No weapon masteries yet.</p> `
            : html`
              <div class="d-flex flex-wrap gap-3">
                ${this._masteries.map(
                  (mastery) => html`
                    <div style="flex: 1 1 280px; min-width: 260px; max-width: 320px;">
                      <div class="card h-100">
                        <div class="card-body">
                          <div class="d-flex justify-content-between align-items-start mb-2">
                            <h6 class="card-title mb-0">${mastery.name}</h6>
                            <button
                              class="btn btn-sm btn-outline-primary"
                              title="Edit"
                              @click="${() => this._editMastery(mastery.slug)}"
                            >
                              ${pencilIcon}
                            </button>
                          </div>

                          <div class="d-flex align-items-center gap-2 mb-2">
                            <code class="small text-muted">${mastery.slug}</code>
                            <button
                              class="btn btn-sm btn-outline-secondary py-0 px-2"
                              title="Copy Slug"
                              @click="${(/** @type {Event} */ e) => this._copyToClipboard(mastery.slug, e)}"
                            >
                              ${clipboardIcon}
                            </button>
                          </div>

                          <div class="mb-2">
                            <span class="badge bg-secondary">v${mastery.version || 1}</span>
                            ${
                              mastery.published
                                ? html` <span class="badge bg-success">Published</span> `
                                : html` <span class="badge bg-warning text-dark">Draft</span> `
                            }
                          </div>

                          ${
                            mastery.description
                              ? html`
                                <p class="small text-muted mb-0">${this._truncateDescription(mastery.description)}</p>
                              `
                              : ''
                          }
                        </div>
                      </div>
                    </div>
                  `,
                )}
              </div>
            `
      }
    `;
  }
}

customElements.define('staff-weapon-masteries-view', StaffWeaponMasteriesView);
